import { useMemo } from "react";
import { CaseInfoType, AlertDataType } from "lib/types";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import {
  formatAlertTagSDKToDisplayWithDesc,
  formatCaseStatusSDKToDisplay,
  getCaseStatusStyles,
  formatMinute,
} from "lib/utils";
import moment from "moment";

import { CASE_STATUS_SDK, INVESTIGATE_TAG_SDK } from "lib/constants";

const getCaseStatusTextColor = (status: CASE_STATUS_SDK | "Case Detected") => {
  switch (status) {
    case "Case Detected":
      return { color: "#222222" };
    case CASE_STATUS_SDK.Investigate:
      return { color: "#FF9800" };
    case CASE_STATUS_SDK.CallPd:
      return { color: "#5E4DB2" };
    case CASE_STATUS_SDK.Resolved:
      return { color: "#3A855D" };
    case CASE_STATUS_SDK.Close:
      return { color: "#3E83F6" };
    default:
      return { color: "#222222" };
  }
};

const styles = StyleSheet.create({
  page: {
    // flexDirection: "column",
    // padding: 30,
    // justifyContent: "center",
    // alignItems: "center",
    // height: "100%",
    //fontFamily: "NotoSans",
    width: 1200,
    padding: 20,
  },
  sectionTop: {
    marginTop: 8,
    flexDirection: "row",
    justifyContent: "center",
    fontSize: 8,
  },

  title: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
    textAlign: "center",
  },

  tag: {
    backgroundColor: "#EEEEEE",
    color: "#737373",
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  tableTitle: {
    display: "flex",
    flexDirection: "row",
    marginTop: 10,
    fontSize: 7,
    color: "#717171",
    gap: 40,
  },
  sectionPrefix: {
    width: 2,
    height: 10,
    marginRight: 4,
    backgroundColor: "#3E83F6",
  },
  section: {
    textAlign: "left",
    fontSize: 12,
  },
  image: {
    fontSize: 8,
    maxWidth: 300,
    width: 300,
    textAlign: "center",
  },
  line: {
    borderBottom: 1,
    borderColor: "#EEEEEE",
    // marginTop: 10,
    width: "100%",
    marginVertical: 5,
  },
  tableContent: {
    fontSize: 8,
    display: "flex",
    flexDirection: "row",
    color: "#222222",
    gap: 40,
    //justifyContent: "space-between",
  },
  tableCol1: {
    maxWidth: 60,
    width: 60,
    flexGrow: 0,
    flexShrink: 0,
    //color: "#222222",
  },
  noteTitle: {
    maxWidth: 60,
    width: 60,
    flexGrow: 0,
    flexShrink: 0,
    marginTop: 10,
  },
  tableCol2: {
    flexGrow: 0,
    flexShrink: 0,
    maxWidth: 100,
    width: 100,
  },
  action: {
    flexGrow: 0,
    flexShrink: 0,
    maxWidth: 100,
    width: 100,
  },
  tableNote: {
    maxWidth: 100,
    width: 100,
    flexGrow: 0,
    flexShrink: 0,
    marginTop: 10,
  },
  timelineNote: {
    maxWidth: 400,
    width: 400,
    flexGrow: 0,
    flexShrink: 0,
    marginRight: 10,
  },
});
type DataType = {
  time: number;
  action: string;
  note?: string;
  noteOwner?: string;
};
const PdfDocument = (props: {
  caseInfo: CaseInfoType;
  alertSnapshotMap: Map<string, string>;
  archiveSnapshotMap: Map<string, string>;
}) => {
  const { caseInfo, alertSnapshotMap, archiveSnapshotMap } = props;
  const timelineData = useMemo(() => {
    if (!caseInfo) {
      return;
    }
    const tableData: DataType[] = [];
    caseInfo.alerts.forEach((alert: AlertDataType) => {
      const alertNotes = alert?.notes?.map(({ createdAt, note, user }) => {
        return {
          time: 1000 * createdAt,
          note,
          noteOwner: user.name,
          action: "Add Alert Note",
        };
      });
      tableData.push(...alertNotes);
    });
    const caseNotes = caseInfo?.notes?.map(({ createdAt, note, user }) => {
      return {
        time: 1000 * createdAt,
        note,
        noteOwner: user.name,
        action: "Add Case Note",
      };
    });
    const caseHistory = caseInfo?.statusHistory?.map(
      ({ createdAt, status }) => {
        return {
          time: +createdAt,
          action: status,
        };
      },
    );

    tableData.push(...caseNotes);
    tableData.push({
      time: +caseInfo?.alerts?.at(0)?.createdAt,
      action: "Case Detected",
    });
    tableData.push(...caseHistory);

    tableData.sort((a, b) => a.time - b.time);
    //setTimelineData(tableData);
    return tableData;
  }, [caseInfo]);

  return (
    <Document title={"Case Report: " + caseInfo.displayId} pageMode="useNone">
      <Page size="A4" style={styles.page}>
        <View>
          <Text style={styles.title}>Case Report: {caseInfo.displayId}</Text>
        </View>
        <View style={styles.sectionTop}>
          {/* <SvgCaseCustomer width="17" style={{ fill: "#7171a" }} /> */}
          {caseInfo.customer?.accountName && (
            <Text style={{ marginRight: 12 }}>
              {caseInfo.customer?.accountName}
            </Text>
          )}

          {/* <SvgMapMarker width="16" style={{ fill: "#7171a" }} /> */}

          <Text style={{ marginRight: 2 }}>
            {caseInfo?.alerts?.at(0)?.site?.name}
          </Text>
          <Text style={{ marginRight: 0, color: "#BDBDBD" }}>{" |"}</Text>
          <Text style={{ overflow: "hidden" }}>
            {caseInfo?.alerts?.at(0)?.location?.address}
          </Text>
          <Text style={{ marginRight: 12 }}>
            {caseInfo?.alerts?.at(0)?.location?.zipcode}
          </Text>
          <Text>{moment(+caseInfo?.createdAt).format("MMM DD[,] yyyy")}</Text>
        </View>
        <View style={styles.sectionTop}>
          <Text
            style={Object.assign(
              getCaseStatusStyles(caseInfo?.status as CASE_STATUS_SDK),
              { paddingHorizontal: 8, borderRadius: 4, marginRight: 8 },
            )}
          >
            {formatCaseStatusSDKToDisplay(caseInfo?.status as CASE_STATUS_SDK)}
          </Text>
          <Text style={styles.tag}>
            {formatAlertTagSDKToDisplayWithDesc(
              caseInfo?.tag?.name as INVESTIGATE_TAG_SDK,
              caseInfo?.tag?.desc,
            )}
          </Text>
        </View>
        <View
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            marginTop: 16,
          }}
        >
          <View style={styles.sectionPrefix} />
          <Text style={styles.section}>Section 1 - Alerts</Text>
        </View>
        <View style={styles.tableTitle}>
          <Text style={styles.tableCol1}>Time </Text>
          <Text style={styles.tableCol2}>Camera Name</Text>
          <Text>Snapshot</Text>
        </View>
        <View style={styles.line} />
        {caseInfo?.alerts?.map((alert) => (
          <>
            <View style={styles.tableContent}>
              <View style={styles.tableCol1}>
                <Text style={styles.tableCol1}>
                  {formatMinute(Number(alert?.createdAt))}
                </Text>
                {alert?.notes?.length > 0 && (
                  <Text style={styles.noteTitle}>Note</Text>
                )}
              </View>
              <View style={styles.tableCol2}>
                <Text style={styles.tableCol2}>{alert?.camera?.name}</Text>
                {alert?.notes?.map((note) => (
                  <Text style={styles.tableNote}>{note.note}</Text>
                ))}
              </View>
              <View>
                {alertSnapshotMap?.get(alert.id) ? (
                  <Image
                    style={styles.image}
                    src={alertSnapshotMap?.get(alert.id)} // 替换为你的图片 URL
                  />
                ) : (
                  <Text style={styles.image}>
                    The image does not exist or has expired{" "}
                  </Text>
                )}
              </View>
            </View>
            <View style={styles.line} />
          </>
        ))}
        <View
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            marginTop: 16,
          }}
        >
          <View style={styles.sectionPrefix} />
          <Text style={styles.section}>Section 2 - Archives</Text>
        </View>
        <View style={styles.tableTitle}>
          <Text style={styles.tableCol1}>Time </Text>
          <Text style={styles.tableCol2}>Camera Name</Text>
          <Text>Snapshot</Text>
        </View>
        <View style={styles.line} />
        {caseInfo?.archives?.map((archive) => (
          <>
            <View style={styles.tableContent}>
              <View style={styles.tableCol1}>
                <Text style={styles.tableCol1}>
                  {formatMinute(Number(archive?.createdAt))}
                </Text>
              </View>
              <View style={styles.tableCol2}>
                <Text style={styles.tableCol2}>
                  {archive?.uploadParams?.cameraName}
                </Text>
              </View>
              <View>
                {archiveSnapshotMap?.get(archive.id) ? (
                  <Image
                    style={styles.image}
                    src={archiveSnapshotMap?.get(archive.id)}
                  />
                ) : (
                  <Text style={styles.image}>
                    The image does not exist or has expired{" "}
                  </Text>
                )}
              </View>
            </View>
            <View style={styles.line} />
          </>
        ))}
        <View
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            marginTop: 16,
          }}
        >
          <View style={styles.sectionPrefix} />
          <Text style={styles.section}>Section 3 - Timeline</Text>
        </View>
        <View style={styles.tableTitle}>
          <Text style={styles.tableCol1}>Time </Text>
          <Text style={styles.action}>Actions</Text>
          <Text style={styles.action}>Created By</Text>
          <Text style={styles.timelineNote}>Details</Text>
        </View>
        <View style={styles.line} />
        {timelineData?.map((data) => (
          <>
            <View style={styles.tableContent}>
              <Text style={styles.tableCol1}>
                {moment(data.time).format("hh:mm A")}
              </Text>
              <Text
                style={Object.assign(
                  getCaseStatusTextColor(data.action as CASE_STATUS_SDK),
                  styles.action,
                )}
              >
                {formatCaseStatusSDKToDisplay(data.action as CASE_STATUS_SDK)}
              </Text>
              <Text style={styles.tableCol1}>{data?.noteOwner || ""}</Text>
              <Text style={styles.timelineNote}>{data?.note || ""}</Text>
            </View>
            <View style={styles.line} />
          </>
        ))}
      </Page>
    </Document>
  );
};

export default PdfDocument;
